import {
  IOsiFlatOsvWidgetViewModel,
  IOsiFlatOsvWidgetViewModelToken
} from '@shared/types/mobx/widgets/OsiFlatOsvWidget';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'inversify-react';
import { tokens, TranslatedToken, useTranslation } from '@shared/utils/i18n';
import { Autocomplete, Box, Button, Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { OsvGrid } from '@shared/common/OsvGrid';
import { darken, lighten } from '@mui/material/styles';
import { useMemo, useEffect } from 'react';
import { GridValueFormatterParams } from '@mui/x-data-grid-pro';
import { formatOsvMoney } from '@shared/utils/helpers/osvMoney';
// OSI-808: общие минимальные ширины колонок ОСВ (обоснование чисел — в самом файле)
import { OSV_MONEY_COL_MIN_WIDTH, OSV_TITLE_COL_MIN_WIDTH } from '@shared/utils/helpers/osvColumnWidths';
import i18next from 'i18next';

const getBackgroundColor = (color: any, mode: any) => (mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6));

const getHoverBackgroundColor = (color: any, mode: any) =>
  `${mode === 'dark' ? darken(color, 0.5) : lighten(color, 0.5)} !important`;

export const OsiFlatOsvWidget: React.FC = observer(() => {
  const vm = useInjection<IOsiFlatOsvWidgetViewModel>(IOsiFlatOsvWidgetViewModelToken);

  const { translateToken: tt } = useTranslation();

  const columns = useMemo(
    () => [
      {
        field: 'period',
        headerName: tt(tokens.osiOSV.table.period),
        // OSI-808: 160 -> 240, иначе названия категорий услуг обрезались («Текущие взносы н…»)
        minWidth: OSV_TITLE_COL_MIN_WIDTH,
        valueGetter: ({ row }: { row: any }) => {
          const { period } = row;
          return period[period.length - 1];
        },
        flex: 0.3
      },
      {
        field: 'totalBegin',
        headerName: tt(tokens.osiOSV.table.totalBegin),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        // OSI-808: 50 -> 125, иначе крупные суммы обрезались («65 270 007…»); константа общая для всех экранов ОСВ
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        // Форматтер денег: округляет до 2 знаков и убирает артефакт «-0» при свёрнутых дочерних помещениях
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalDebet',
        headerName: tt(tokens.osiOSV.table.totalDebt),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalFixes',
        headerName: tt(tokens.osiOSV.table.totalFixes),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalKredit',
        headerName: tt(tokens.osiOSV.table.totalKredit),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalFine',
        headerName: tt(tokens.osiOSV.table.totalFine),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalEnd',
        headerName: tt(tokens.osiOSV.table.totalEnd),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      }
    ],
    [tt]
  );

  useEffect(() => {
    if (!vm.selectedAbonent) return;

    vm.loadOsv();
  }, [vm.selectedAbonent, vm, i18next.language]);

  // Ранний return <LoadingScreen /> УБРАН: он размонтировал весь виджет на время загрузки
  // (моргание + потеря фокуса/состояния чекбокса). Теперь загрузка показывается неблокирующим
  // overlay внутри DataGridPro (см. isLoading у OsvGrid ниже) — разметка остаётся на месте.

  return (
    <>
      <Box sx={{ mt: 2 }} display="flex" alignItems="center">
        <Grid container spacing={1}>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              fullWidth
              disableClearable
              getOptionLabel={(option) => option.flat}
              renderOption={(props, option) => <li {...props}>{option.flat}</li>}
              options={vm.visibleAbonents.slice()?.sort((a, b) => flatComparator(a.flat, b.flat))}
              onChange={(event, value) => {
                vm.selectedAbonent = value;
              }}
              // @ts-expect-error autocomplete work with null
              value={vm.selectedAbonent}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tt(tokens.common.formFields.selectFlat.label)}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    autoComplete: 'new-password'
                  }}
                  style={{ margin: 0 }}
                />
              )}
            />
          </Grid>
          {/* Чекбокс перенесён вправо от кнопки «Скачать» — теперь он внутри Box с кнопками, после кнопки скачивания */}
          <Grid item xs={12} sm={7}>
            <Box display="flex" alignItems="center" sx={{ height: '100%' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={vm.loadOsv}
                size="medium"
                disabled={!vm.selectedAbonent}
              >
                <TranslatedToken id={tokens.common.refresh} />
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudDownloadIcon />}
                onClick={vm.downloadOsv}
                sx={{ ml: 1 }}
                size="medium"
                disabled={!vm.selectedAbonent}
              >
                <TranslatedToken id={tokens.common.download} />
              </Button>
              {/* Чекбокс «Свернуть дочерние помещения»: скрывает дочерние помещения из выбора и вливает их суммы в родителя; размещён правее кнопки «Скачать».
                  OSI-806: показываем ТОЛЬКО если у ОСИ реально есть дочерние помещения (vm.hasChildAbonents,
                  флаг приходит с бэка в osiInfo). В домах без паркингов/кладовок чекбокс бессмыслен.
                  Значение vm.isCollapseChildren при этом НЕ сбрасываем — сворачивать всё равно нечего. */}
              {vm.hasChildAbonents && (
                <FormControlLabel
                  sx={{ ml: 1 }}
                  control={
                    <Checkbox
                      checked={vm.isCollapseChildren}
                      onChange={(e) => vm.setCollapseChildren(e.target.checked)}
                    />
                  }
                  label={tt(tokens.osiOSV.collapseChildren)}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          height: '60vh',
          '& .row-child--level-1': {
            bgcolor: (theme) => getBackgroundColor(theme.palette.info.main, theme.palette.mode),
            '&:hover': {
              bgcolor: (theme) => getHoverBackgroundColor(theme.palette.info.main, theme.palette.mode)
            }
          }
        }}
      >
        <OsvGrid
          columns={columns}
          rows={vm.rows?.map((e, id) => ({ id, ...e }))}
          totals={vm.totalsOsv}
          rowCount={vm.periodsNumber}
          getTreeDataPath={(row) => row.period}
          // Неблокирующий loading overlay вместо размонтирующего LoadingScreen
          isLoading={vm.isLoading}
        />
      </Box>
    </>
  );
});
